import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, Plus, FileText, Clock, User, Settings, Users, LogOut, Activity, Archive, CheckSquare, BarChart, Trash2, MapPinned } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { api, auth } from "../lib/api";
import { StatCard } from "../components/StatCard";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useLanguage } from "../context/LanguageContext";

const statusLabels = {
  created: { className: "bg-slate-100 text-slate-700 border-slate-200" },
  audio_uploaded: { className: "bg-blue-50 text-blue-700 border-blue-200" },
  transcribed: { className: "bg-amber-50 text-amber-700 border-amber-200" },
  analyzed: { className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};



export default function Dashboard() {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const [stats, setStats] = useState(null);
  const [caseToDelete, setCaseToDelete] = useState(null);
  const user = auth.getUser();
  const { t } = useLanguage();

  useEffect(() => {
    loadCases();
    if (auth.isAdmin()) {
      loadStats();
    }
  }, []);

  const loadStats = async () => {
    try {
      const res = await api.getDashboardStats();
      setStats(res.data);
    } catch (e) {
      console.error("Failed to load stats", e);
    }
  };

  const loadCases = async (searchTerm = "") => {
    try {
      setLoading(true);
      const response = await api.getCases(searchTerm);
      setCases(response.data);
    } catch (error) {
      console.error("Failed to load cases:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadCases(search);
  };

  const handleLogout = () => {
    auth.logout();
  };

  const getStatusBadge = (status) => {
    // Map submitted back to analyzed for display
    const effectiveStatusKey = status === "submitted" ? "analyzed" : status;
    const config = statusLabels[effectiveStatusKey] || statusLabels.created;

    // We should strictly use the status as key suffix if valid, simplified:
    const finalStatus = statusLabels[effectiveStatusKey] ? effectiveStatusKey : 'created';

    return (
      <Badge variant="outline" className={`status-pill ${config.className}`}>
        {t(`status_${finalStatus}`)}
      </Badge>
    );
  };

  const confirmDeleteCase = async () => {
    if (!caseToDelete) return;
    try {
      await api.deleteCase(caseToDelete);
      toast.success(t('assessment_deleted_toast'));
      loadCases(search);
      if (auth.isAdmin()) loadStats();
    } catch (error) {
      console.error("Failed to delete case:", error);
      toast.error(t('failed_delete_assessment_toast'));
    } finally {
      setCaseToDelete(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']" data-testid="dashboard-title">
                {t('app_title')}
              </h1>
              <p className="text-sm text-slate-500 mt-0.5">{t('dashboard_subtitle')}</p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate("/new-case")}
                className="bg-slate-700 hover:bg-slate-800 text-white rounded-sm px-6"
                data-testid="new-assessment-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('new_assessment')}
              </Button>

              {auth.isAdmin() && (
                <>
                  <Link to="/users">
                    <Button variant="ghost" size="sm" className="hidden md:flex gap-2">
                      <Users className="w-4 h-4" />
                      {t('manage_case_workers')}
                    </Button>
                  </Link>
                  <Link to="/analytics/geo">
                    <Button variant="ghost" size="sm" className="hidden md:flex gap-2">
                      <MapPinned className="w-4 h-4" />
                      {t('analytics')}
                    </Button>
                  </Link>
                </>
              )}

              <LanguageSwitcher />

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="rounded-sm gap-2" data-testid="user-menu-btn">
                    <User className="w-4 h-4" />
                    {user?.name}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5 text-sm text-slate-500">
                    {user?.email}
                    <div className="text-xs capitalize text-slate-400">
                      {(user?.role === "user" || user?.role === "caseworker") ? "case worker" : user?.role?.replace("_", " ")}
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  {auth.isAdmin() && (
                    <DropdownMenuItem onClick={() => navigate("/users")} data-testid="users-menu-item">
                      <Users className="w-4 h-4 mr-2" />
                      {t('manage_case_worker_menu')}
                    </DropdownMenuItem>
                  )}
                  {auth.isSuperAdmin() && (
                    <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="settings-menu-item">
                      <Settings className="w-4 h-4 mr-2" />
                      {t('ai_settings_menu')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-menu-item">
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('sign_out')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">

        {/* Admin Stats */}
        {auth.isAdmin() && stats && (
          <div className="mb-10 space-y-6 animate-in fade-in slide-in-from-top-4 duration-500">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard title={t('total_case_workers')} value={stats.summary.total_users} icon={Users} color="bg-blue-50 text-blue-600" />
              <StatCard title={t('total_cases')} value={stats.summary.total_cases} icon={FileText} color="bg-purple-50 text-purple-600" />
              <StatCard title={t('completed')} value={stats.summary.by_status?.analyzed || 0} icon={CheckSquare} color="bg-green-50 text-green-600" />
              <StatCard title={t('active_users')} value={stats.user_activity?.filter(u => u.cases_created > 0).length} icon={Activity} color="bg-amber-50 text-amber-600" />
            </div>

            <Card className="border-slate-200">
              <CardHeader className="border-b border-slate-100 py-4 px-6">
                <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                  <BarChart className="w-4 h-4" /> {t('case_worker_activity')}
                </CardTitle>
              </CardHeader>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-slate-50 text-slate-500 border-b border-slate-100">
                    <tr>
                      <th className="px-6 py-3 font-semibold">{t('case_worker')}</th>
                      <th className="px-6 py-3 font-semibold text-center">{t('role')}</th>
                      <th className="px-6 py-3 font-semibold text-center">{t('cases')}</th>
                      <th className="px-6 py-3 font-semibold text-center">{t('status_analyzed')}</th>
                      <th className="px-6 py-3 font-semibold text-center">{t('recordings')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {stats.user_activity.map(u => (
                      <tr key={u.id} className="hover:bg-slate-50/50">
                        <td className="px-6 py-3">
                          <div className="font-medium text-slate-900">{u.name}</div>
                          <div className="text-xs text-slate-500">{u.email}</div>
                        </td>
                        <td className="px-6 py-3 text-center capitalize text-slate-600">
                          {(u.role === "user" || u.role === "caseworker") ? "case worker" : u.role.replace("_", " ")}
                        </td>
                        <td className="px-6 py-3 text-center">{u.cases_created}</td>
                        <td className="px-6 py-3 text-center">
                          {u.cases_submitted > 0 ? (
                            <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-xs font-semibold">{u.cases_submitted}</span>
                          ) : (
                            <span className="text-slate-400">0</span>
                          )}
                        </td>
                        <td className="px-6 py-3 text-center text-slate-600">{u.recordings_uploaded}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* Search */}
        <form onSubmit={handleSearch} className="mb-8">
          <div className="flex gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder={t('search_placeholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-11 rounded-sm border-slate-300 bg-white"
                data-testid="search-input"
              />
            </div>
            <Button
              type="submit"
              variant="secondary"
              className="rounded-sm border-slate-300"
              data-testid="search-btn"
            >
              {t('search_button')}
            </Button>
          </div>
        </form>

        {/* Cases List */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-800 font-['Manrope']">
              {t('recent_assessments')}
            </h2>
            <span className="text-sm text-slate-500">{t('count_cases').replace('{count}', cases.length)}</span>
          </div>

          {loading ? (
            <div className="text-center py-12 text-slate-500">{t('loading_cases')}</div>
          ) : cases.length === 0 ? (
            <Card className="border-slate-200 rounded-sm">
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-700 mb-2">{t('no_assessments')}</h3>
                <p className="text-slate-500 mb-6">{t('create_first')}</p>
                <Button
                  onClick={() => navigate("/new-case")}
                  className="bg-slate-700 hover:bg-slate-800 rounded-sm"
                  data-testid="empty-new-assessment-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('new_assessment')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {cases.map((caseItem) => (
                <Link
                  key={caseItem.id}
                  to={`/case/${caseItem.id}`}
                  className="block"
                  data-testid={`case-card-${caseItem.id}`}
                >
                  <Card className="border-slate-200 rounded-sm hover:shadow-md transition-shadow cursor-pointer">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-sm bg-slate-100 flex items-center justify-center">
                            <User className="w-5 h-5 text-slate-500" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900">
                              {caseItem.client_name}
                              <span className="ml-2 text-xs font-medium text-slate-500">{t('assessment_number')} {caseItem.assessment_number || 1}</span>
                            </h3>
                            <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                              <span className="font-mono">{caseItem.client_id}</span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5" />
                                {caseItem.assessment_date}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {getStatusBadge(caseItem.status)}
                          {auth.isAdmin() && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-slate-400 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                              onClick={(e) => {
                                e.preventDefault(); // Prevent link navigation
                                setCaseToDelete(caseItem.id);
                              }}
                              title={t('delete_assessment_tooltip')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>

      </main >

      <AlertDialog open={!!caseToDelete} onOpenChange={(open) => !open && setCaseToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('delete_assessment_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('delete_assessment_desc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteCase} className="bg-red-600 hover:bg-red-700">
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div >
  );
}
